import { useState, useCallback, useEffect, useRef } from 'react';
import { Box, Text } from '@/components/primitives';
import { Input } from '@/components/primitives';
import { ActionButton } from '@/components/common/ActionButton';
import { useApiMutation } from '@/hooks/useApiMutation';
import { changePassword } from '@/api';
import { Actions } from '@/theme';
import { validatePassword } from '../../utils/validation';

export interface PasswordChangeFormProps {
  /** Called after successful password change. */
  onComplete?: () => void;
}

export function PasswordChangeForm({ onComplete }: PasswordChangeFormProps) {
  const [showEditor, setShowEditor] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);

  useEffect(() => {
    if (newPassword) {
      const errs = validatePassword(newPassword);
      if (currentPassword && newPassword === currentPassword) {
        errs.push('New password must differ from the current password');
      }
      setPasswordErrors(errs);
    } else {
      setPasswordErrors([]);
    }
  }, [newPassword, currentPassword]);

  // Bug-hunt #74: synchronous in-flight guard. React state's
  // `mutation.isPending` doesn't flip between rapid same-tick clicks
  // (no re-render fires in between), so three quick clicks can all see
  // isPending=false and three changePassword PUTs reach the server. A
  // ref set BEFORE `mutate()` collapses them to one.
  const submitInFlightRef = useRef(false);

  // #878: no custom `onError` here. A caller's onError REPLACES the hook's
  // default push, which silently dropped the server's 400 `detail` ("Current
  // password is incorrect") and left Save looking dead. The ref is released in
  // `onSettled` (success and error) so the default 4xx path reports into the
  // Edit Profile dialog's band; 5xx stays with routeMutationError.
  const mutation = useApiMutation({
    mutationFn: changePassword,
    onSuccess: () => {
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordErrors([]);
      setShowEditor(false);
      onComplete?.();
    },
    onSettled: () => {
      submitInFlightRef.current = false;
    },
    successMessage: 'Password updated successfully.',
    errorMessage: 'Failed to update password.',
  });

  const handleSubmit = useCallback(() => {
    if (submitInFlightRef.current) return;
    if (
      currentPassword &&
      newPassword &&
      confirmPassword &&
      newPassword === confirmPassword &&
      passwordErrors.length === 0
    ) {
      submitInFlightRef.current = true;
      mutation.mutate({ currentPassword, newPassword, reNewPassword: confirmPassword });
    }
  }, [currentPassword, newPassword, confirmPassword, passwordErrors.length, mutation]);

  const handleCancel = useCallback(() => {
    setShowEditor(false);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordErrors([]);
  }, []);

  const passwordsMatch = newPassword === confirmPassword;
  const isValid =
    currentPassword.length > 0 &&
    newPassword.length > 0 &&
    confirmPassword.length > 0 &&
    passwordsMatch &&
    passwordErrors.length === 0;

  if (!showEditor) {
    return (
      <Box display="flex" justifyContent="center">
        <ActionButton
          action={Actions.resetPassword}
          mode="subtle"
          onClick={() => setShowEditor(true)}
          label="Change Password"
        />
      </Box>
    );
  }

  return (
    <Box>
      <Text variant="smallLabel" color="secondary" style={{ display: 'block', marginBottom: 2 }}>
        Change Password
      </Text>
      <Box display="flex" flexDirection="column" gap="ms">
        <Input
          type="password"
          label="Current Password"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          disabled={mutation.isPending}
        />
        <Input
          type="password"
          label="New Password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          error={passwordErrors.length > 0}
          helperText={passwordErrors.length > 0 ? passwordErrors[0] : '8-50 chars, lowercase, number, special (#$^+=!*()@%&)'}
          disabled={mutation.isPending}
        />
        <Input
          type="password"
          label="Confirm Password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          error={confirmPassword.length > 0 && !passwordsMatch}
          helperText={confirmPassword.length > 0 && !passwordsMatch ? 'Passwords must match' : ''}
          disabled={mutation.isPending}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && isValid) {
              e.preventDefault();
              handleSubmit();
            }
          }}
        />
        <Box display="flex" gap="sm">
          <ActionButton
            action={Actions.save}
            mode="dialog-primary"
            onClick={handleSubmit}
            // Bug-hunt #74: disable explicitly during the pending
            // mutation so a fast double-click (or Enter while loading)
            // can't fire a second changePassword request that races the
            // first. `loading` shows the spinner but doesn't guarantee
            // pointer-event disable in every ActionButton mode.
            disabled={!isValid || mutation.isPending}
            loading={mutation.isPending}
            label="Update Password"
          />
          <ActionButton
            action={Actions.cancel}
            mode="dialog-secondary"
            onClick={handleCancel}
            disabled={mutation.isPending}
            label="Cancel"
          />
        </Box>
      </Box>
    </Box>
  );
}
